import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Mail, Shield, Loader2, Copy, CheckCircle2, Clock, Trash2 } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

export default function Staff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "station_admin", station_ids: [] });
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = async () => {
    setLoading(true);
    const [u, i, s] = await Promise.all([api.get("/users"), api.get("/invitations"), api.get("/stations")]);
    setUsers(u.data); setInvites(i.data); setStations(s.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleStation = (id) =>
    setForm((f) => ({ ...f, station_ids: f.station_ids.includes(id) ? f.station_ids.filter((x) => x !== id) : [...f.station_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/invitations", form);
      setLastLink(data.invite_link);
      toast.success(`Invitation sent to ${data.email}`);
      setForm({ email: "", name: "", role: "station_admin", station_ids: [] });
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSending(false);
  };

  const roleBadge = (r) => ({
    manager: "bg-emerald-500/15 text-emerald-400",
    station_admin: "bg-blue-500/15 text-blue-400",
    staff: "bg-violet-500/15 text-violet-400",
  })[r] || "bg-white/10 text-white";

  const delUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? They lose access immediately.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const delInvite = async (i) => {
    if (!window.confirm(`Delete the invitation for ${i.email}?`)) return;
    try { await api.delete(`/invitations/${i.id}`); toast.success("Invitation deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Access Control</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Staff &amp; Invitations</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-staff-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Staff
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-[#374151] text-white">
            <DialogHeader><DialogTitle>Invite station staff</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div><Label className="text-[#9CA3AF]">Full name</Label>
                <Input required data-testid="invite-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
              <div><Label className="text-[#9CA3AF]">Email</Label>
                <Input type="email" required data-testid="invite-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
              <div><Label className="text-[#9CA3AF]">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role-select" className="mt-1.5 bg-[#0B0F17] border-[#374151]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#374151] text-white">
                    <SelectItem value="station_admin">Station Admin</SelectItem>
                    <SelectItem value="staff">Shift Staff</SelectItem>
                    <SelectItem value="manager">Manager (full access)</SelectItem>
                  </SelectContent>
                </Select></div>
              {form.role !== "manager" && (
                <div>
                  <Label className="text-[#9CA3AF]">Assign stations</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {stations.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`invite-station-${s.id}`}>
                        <Checkbox checked={form.station_ids.includes(s.id)} onCheckedChange={() => toggleStation(s.id)} />
                        {s.name}
                      </label>
                    ))}
                    {!stations.length && <div className="text-xs text-[#9CA3AF]">No stations yet.</div>}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={sending} data-testid="invite-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Send invitation</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lastLink && (
        <div className={`${card} p-4 flex items-center justify-between gap-3`} data-testid="invite-link-box">
          <div className="min-w-0">
            <div className="text-xs text-[#9CA3AF]">Invite link (also emailed)</div>
            <div className="text-sm font-mono text-emerald-400 truncate">{lastLink}</div>
          </div>
          <Button size="sm" variant="outline" className="border-[#374151] bg-transparent text-white shrink-0"
            onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${card} p-5 sm:p-6`} data-testid="users-panel">
            <div className="flex items-center gap-2 mb-4"><Shield className="w-4 h-4 text-emerald-400" /><h3 className="font-[Outfit] font-semibold text-lg">Active Users</h3></div>
            <div className="divide-y divide-white/5">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-3" data-testid={`user-row-${u.id}`}>
                  <div>
                    <div className="text-sm text-white">{u.name} {u.username && <span className="text-[#9CA3AF] text-xs">@{u.username}</span>}</div>
                    <div className="text-xs text-[#9CA3AF]">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-mono px-2 py-1 rounded uppercase ${roleBadge(u.role)}`}>{u.role.replace("_", " ")}</span>
                    {me?.email !== u.email && (
                      <button onClick={() => delUser(u)} data-testid={`delete-user-${u.id}`} className="text-[#9CA3AF] hover:text-red-400" title="Remove user"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`${card} p-5 sm:p-6`} data-testid="invites-panel">
            <div className="flex items-center gap-2 mb-4"><Mail className="w-4 h-4 text-amber-400" /><h3 className="font-[Outfit] font-semibold text-lg">Invitations</h3></div>
            <div className="divide-y divide-white/5">
              {invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between py-3" data-testid={`invite-row-${i.id}`}>
                  <div>
                    <div className="text-sm text-white">{i.name}</div>
                    <div className="text-xs text-[#9CA3AF]">{i.email} · {i.role.replace("_", " ")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-mono px-2 py-1 rounded uppercase flex items-center gap-1 ${i.status === "accepted" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {i.status === "accepted" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{i.status}
                    </span>
                    <button onClick={() => delInvite(i)} data-testid={`delete-invite-${i.id}`} className="text-[#9CA3AF] hover:text-red-400" title="Delete invitation"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {!invites.length && <div className="text-sm text-[#9CA3AF] py-6 text-center">No invitations sent yet.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
